import { default as React } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import { Entity, RELATION_OWNED_BY } from '@backstage/catalog-model';

interface AccessDeniedModalProps {
  entity: Entity | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal shown when an unauthorized user clicks a restricted (Custom visibility) microapp card.
 * Displays the app name, owner contact info, and a link to browse all apps.
 */
export const AccessDeniedModal = ({
  entity,
  open,
  onClose,
}: AccessDeniedModalProps) => {
  const appName =
    entity?.metadata?.title || entity?.metadata?.name || 'this app';

  const ownerEmail =
    entity?.metadata?.annotations?.['microapp/createdBy'] ||
    entity?.metadata?.annotations?.['microapp/owners']?.split(',')[0]?.trim() ||
    '';

  const ownerDisplay =
    entity?.relations
      ?.find(r => r.type === RELATION_OWNED_BY)
      ?.targetRef?.split('/')
      .pop() || ownerEmail;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="access-denied-dialog-title"
    >
      <DialogTitle id="access-denied-dialog-title">
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <LockIcon style={{ color: '#e65100' }} />
          <span>Restricted Access</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          <strong>{appName}</strong> has restricted access. You are not on the
          allowed users list for this app.
        </Typography>
        {ownerEmail && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            Contact the owner to request access:{' '}
            <a href={`mailto:${ownerEmail}`}>{ownerDisplay || ownerEmail}</a>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Dismiss access denied dialog">
          Dismiss
        </Button>
        <Button
          variant="contained"
          color="primary"
          href="/catalog?filters%5Bkind%5D=Component&filters%5Btype%5D=microapp"
        >
          Browse All Apps
        </Button>
      </DialogActions>
    </Dialog>
  );
};
